<template>
  <main class="demo-page">
    <header class="demo-bar">
      <div class="demo-bar-top">
        <div class="demo-brand-block">
          <p class="text-caption text-medium-emphasis mb-1">{{ brand }} demo</p>
          <h1 class="text-h6 mb-1">{{ title || 'Opening demo' }}</h1>
          <p v-if="scenarioName" class="text-body-2 mb-0">{{ scenarioName }}</p>
          <p v-if="characters.length" class="text-body-2 mb-0">
            Characters in this link:
            <span v-for="(character, index) in characters" :key="character.id">
              {{ character.name }}<span v-if="index < characters.length - 1">, </span>
            </span>
          </p>
          <p class="text-caption text-medium-emphasis mb-0">{{ expiryLabel }}</p>
        </div>
        <div v-if="partner" class="demo-partner" :aria-label="`${partner.label} partner`">
          <img
            class="demo-partner-logo"
            :src="partner.logoSrc"
            :alt="partner.label"
          >
        </div>
      </div>
    </header>
    <section v-if="framed" class="demo-message">
      <p>The simulation left this demo. Open it again in this tab.</p>
      <v-btn color="primary" class="text-none mt-3" @click="reopen">Open demo</v-btn>
    </section>
    <p v-else-if="error" class="demo-message" role="alert">{{ error }}</p>
    <section v-else-if="pickRequired" class="demo-message">
      <p class="mb-3">This link includes more than one scenario. Choose which to open (uses one of {{ usesLeft ?? 5 }} remaining opens).</p>
      <div class="demo-pick-grid">
        <v-btn
          v-for="scenario in scenarios"
          :key="scenario.releaseId"
          variant="tonal"
          class="text-none justify-start"
          @click="load(scenario.releaseId)"
        >
          {{ scenario.characterName }} — {{ scenario.title }}
          <span v-if="scenario.versionLabel" class="text-medium-emphasis">&nbsp;({{ scenario.versionLabel }})</span>
        </v-btn>
      </div>
    </section>
    <p v-else-if="!src" class="demo-message">Opening the simulation…</p>
    <iframe
      v-else
      :src="src"
      title="Screen-based simulation demo"
      allow="microphone"
      referrerpolicy="no-referrer"
      sandbox="allow-scripts allow-same-origin allow-forms"
      @load="onFrameLoad"
    />
  </main>
</template>

<script setup>
const route = useRoute()
const brand = ref('ReadyAI')
const partner = ref(null)
const title = ref('')
const scenarioName = ref('')
const characters = ref([])
const scenarios = ref([])
const pickRequired = ref(false)
const expireTs = ref(null)
const usesLeft = ref(null)
const error = ref('')
const src = ref('')
const framed = ref(false)

const expiryLabel = computed(() => {
  if (error.value || framed.value) return ''
  const parts = []
  if (!expireTs.value) parts.push('This link does not expire.')
  else {
    const when = new Date(expireTs.value)
    if (!Number.isNaN(when.getTime())) parts.push(`Available until ${when.toLocaleString()}.`)
  }
  if (usesLeft.value != null) {
    if (usesLeft.value <= 0) parts.push('No uses left after this visit.')
    else if (usesLeft.value === 1) parts.push('1 use left.')
    else parts.push(`${usesLeft.value} uses left.`)
  }
  return parts.join(' ')
})

const reopen = () => {
  const href = window.location.href
  if (window.top) window.top.location.href = href
  else window.location.href = href
}

const load = async (releaseId = null) => {
  const token = route.params.token
  if (!token) {
    error.value = 'This demo link is missing.'
    return
  }
  error.value = ''
  src.value = ''
  pickRequired.value = false
  try {
    const params = new URLSearchParams({ token })
    if (releaseId) params.set('release', releaseId)
    const response = await fetch(`/api/auth/demo?${params.toString()}`, {
      credentials: 'same-origin'
    })
    const body = await response.json()
    if (!response.ok) {
      error.value = body?.response?.error?.message || 'This demo link is not available.'
      return
    }
    const demo = body?.response?.demo
    brand.value = demo?.brand || 'ReadyAI'
    partner.value = demo?.partner || null
    title.value = demo?.title || 'Demo'
    scenarioName.value = demo?.scenarioName || ''
    characters.value = demo?.characters || []
    scenarios.value = demo?.scenarios || []
    expireTs.value = demo?.expireTs || null
    const remaining = Number(demo?.usesLeft)
    usesLeft.value = demo?.usesLeft == null || !Number.isFinite(remaining) ? null : remaining
    document.title = `${title.value} | ${brand.value}`

    if (demo?.pickRequired && scenarios.value.length) {
      pickRequired.value = true
      return
    }
    if (!demo?.baseUrl || !demo?.query) {
      error.value = 'This demo link could not open the simulation.'
      return
    }
    src.value = `${demo.baseUrl}?${demo.query}`
  } catch {
    error.value = 'This demo link could not be opened. Check the connection and try again.'
  }
}

const onFrameLoad = (event) => {
  try {
    const href = event.target?.contentWindow?.location?.href || ''
    if (href.startsWith(window.location.origin)) {
      src.value = ''
      framed.value = true
    }
  } catch {
    // The player is still on its own site.
  }
}

onMounted(() => {
  if (window.self !== window.top) {
    framed.value = true
    return
  }
  load()
})
</script>

<style scoped>
.demo-page {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}
.demo-bar {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.demo-bar-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.demo-brand-block {
  min-width: 0;
  flex: 1 1 auto;
}
.demo-partner {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}
.demo-partner-logo {
  display: block;
  height: 40px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
}
.demo-message {
  padding: 24px 16px;
}
.demo-pick-grid {
  display: grid;
  gap: 8px;
  max-width: 40rem;
}
iframe {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  border: 0;
}

@media (max-width: 600px) {
  .demo-bar-top {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .demo-partner-logo {
    height: 36px;
    max-width: 100%;
  }
}
</style>
